<!-- Рисует столбчатый график с легендой и tooltip для dashboard-метрик. -->
<template>
  <div class="bar-chart" ref="wrap">
    <!-- Легенда с чекбоксами -->
    <div class="bar-chart__legend">
      <BaseCheckbox
        v-for="s in normalizedSeries"
        :key="'cb-' + s.key"
        :model-value="!hiddenSeries[s.key]"
        @update:modelValue="(val) => setSeriesVisible(s.key, val)"
      >
        <span class="legend-item">
          <span
            class="legend-dot"
            :style="{ background: s.color }"
          ></span>
          {{ s.label }}
        </span>
      </BaseCheckbox>
    </div>

    <div v-if="tooManyPoints" class="bar-chart__overflow">
      <span>Слишком много точек для столбчатого графика.</span>
      <span>Выберите группировку по неделям или месяцам.</span>
    </div>

    <div v-else-if="allHidden" class="bar-chart__overflow">
      Все серии скрыты — выберите хотя бы одну
    </div>

    <template v-else>
      <svg
        v-if="chartData"
        :viewBox="`0 0 ${chartData.W} ${chartData.H}`"
        class="bar-chart__svg"
        @mousemove="onMouseMove"
        @mouseleave="tooltip.visible = false"
      >
        <!-- Сетка -->
        <line
          v-for="gl in chartData.gridLines"
          :key="'gl-' + gl.y"
          :x1="chartData.padL"
          :y1="gl.y"
          :x2="chartData.W - chartData.padR"
          :y2="gl.y"
          stroke="#e5e7eb"
          stroke-width="1"
        />
        <text
          v-for="gl in chartData.gridLines"
          :key="'glt-' + gl.y"
          :x="chartData.padL - 8"
          :y="gl.y + 4"
          text-anchor="end"
          class="bar-chart__axis-label"
        >
          {{ formatAxisValue(gl.value) }}
        </text>

        <!-- Нулевая линия -->
        <line
          :x1="chartData.padL"
          :y1="chartData.zeroY"
          :x2="chartData.W - chartData.padR"
          :y2="chartData.zeroY"
          stroke="#d1d5db"
          stroke-width="1.5"
        />

        <!-- Группы баров -->
        <g v-for="(group, gi) in chartData.groups" :key="'g-' + gi">
          <template v-if="group.hasData">
            <rect
              v-for="bar in group.bars"
              v-show="!hiddenSeries[bar.seriesKey]"
              :key="'bar-' + bar.seriesKey"
              :x="bar.x"
              :y="bar.y"
              :width="bar.w"
              :height="bar.h"
              :fill="bar.color"
              :opacity="
                hoveredGroup !== null
                  ? hoveredGroup === gi
                    ? 1
                    : 0.35
                  : 0.85
              "
              rx="2"
            />
          </template>

          <!-- Подпись X - только ненулевые с прореживанием -->
          <text
            v-if="group.hasData && group.showLabel"
            :x="group.centerX"
            :y="chartData.H - chartData.padB + 18"
            text-anchor="middle"
            class="bar-chart__axis-label"
          >
            {{ group.label }}
          </text>

          <!-- Hit-зона -->
          <rect
            :x="group.hitX"
            :y="chartData.padT"
            :width="group.hitW"
            :height="chartData.H - chartData.padT - chartData.padB"
            fill="transparent"
            @mouseenter="group.hasData ? (hoveredGroup = gi) : null"
            @mouseleave="hoveredGroup = null"
          />
        </g>

        <!-- Вертикальная линия тултипа -->
        <line
          v-if="tooltip.visible"
          :x1="tooltip.svgX"
          :y1="chartData.padT"
          :x2="tooltip.svgX"
          :y2="chartData.H - chartData.padB"
          stroke="#d1d5db"
          stroke-width="1"
          stroke-dasharray="4 3"
        />
      </svg>

      <!-- Тултип -->
      <transition name="fade-slide">
        <div
          v-if="tooltip.visible"
          class="bar-chart__tooltip"
          :style="{
            left: tooltip.left + 'px',
            top: tooltip.top + 'px',
          }"
        >
          <span class="bar-chart__tooltip-period">
            {{ tooltip.label }}
          </span>
          <template v-for="s in normalizedSeries">
            <span
              v-if="!hiddenSeries[s.key]"
              :key="'tt-' + s.key"
              class="bar-chart__tooltip-row"
            >
              <span
                class="bar-chart__tooltip-dot"
                :style="{ background: s.color }"
              ></span>
              {{ s.label }}:
              {{ formatTooltipValue(tooltip.values[s.key]) }}
            </span>
          </template>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
const PAD = { T: 16, R: 16, B: 36, L: 60 };
const GRID_COUNT = 4;
const GROUP_GAP_RATIO = 0.3;
const MAX_POINTS = 60;
const MIN_BAR_W = 2;
const MAX_X_LABELS = 12;

export default {
  name: "BarChart",

  props: {
    series: {
      type: Array,
      required: true,
    },
    points: {
      type: Array,
      required: true,
    },
    height: {
      type: Number,
      default: 240,
    },
    formatValue: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      containerWidth: 600,
      hoveredGroup: null,
      hiddenSeries: {},
      tooltip: {
        visible: false,
        svgX: 0,
        left: 0,
        top: 0,
        label: "",
        values: {},
      },
    };
  },

  computed: {
    normalizedSeries() {
      return this.series.map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color || "#4c6ef5",
      }));
    },

    visibleSeries() {
      return this.normalizedSeries.filter(
        (s) => !this.hiddenSeries[s.key]
      );
    },

    allHidden() {
      return this.normalizedSeries.every(
        (s) => this.hiddenSeries[s.key]
      );
    },

    tooManyPoints() {
      return this.points.length > MAX_POINTS;
    },

    chartData() {
      if (
        this.tooManyPoints ||
        !this.points ||
        this.points.length < 1 ||
        this.allHidden
      )
        return null;

      const W = Math.max(this.containerWidth, 200);
      const H = this.height;
      const { T, R, B, L } = PAD;
      const innerW = W - L - R;
      const innerH = H - T - B;
      const n = this.points.length;
      const vCount = Math.max(this.visibleSeries.length, 1);

      const allValues = this.points.flatMap((p) =>
        this.visibleSeries.map((s) => p.values[s.key] ?? 0)
      );
      const minVal = Math.min(
        0,
        ...(allValues.length ? allValues : [0])
      );
      const maxVal = Math.max(
        ...(allValues.length ? allValues : [0]),
        1
      );
      const range = maxVal - minVal || 1;

      const toY = (v) => T + innerH - ((v - minVal) / range) * innerH;
      const zeroY = toY(0);

      const groupW = innerW / n;
      const barAreaW = groupW * (1 - GROUP_GAP_RATIO);
      const barW = Math.max(barAreaW / vCount, MIN_BAR_W);
      const actualBarAreaW = barW * vCount;

      const nonZeroIndexes = this.points
        .map((p, i) => ({ p, i }))
        .filter(({ p }) =>
          this.visibleSeries.some((s) => (p.values[s.key] ?? 0) !== 0)
        )
        .map(({ i }) => i);
      const labelStep = Math.max(
        1,
        Math.ceil(nonZeroIndexes.length / MAX_X_LABELS)
      );
      const labelIndexSet = new Set(
        nonZeroIndexes.filter((_, i) => i % labelStep === 0)
      );

      const groups = this.points.map((p, gi) => {
        const groupStartX = L + gi * groupW;
        const centerX = groupStartX + groupW / 2;
        const barsStartX = centerX - actualBarAreaW / 2;

        const hasData = this.visibleSeries.some(
          (s) => (p.values[s.key] ?? 0) !== 0
        );

        const bars = this.normalizedSeries.map((s) => {
          const visibleIndex = this.visibleSeries.findIndex(
            (vs) => vs.key === s.key
          );
          const value = p.values[s.key] ?? 0;
          const barX =
            visibleIndex >= 0 ? barsStartX + visibleIndex * barW : 0;
          const y = value >= 0 ? toY(value) : zeroY;
          const h = Math.max(Math.abs(toY(value) - zeroY), 1);

          return {
            seriesKey: s.key,
            x: barX,
            y,
            w: Math.max(barW - 1, 1),
            h,
            color: s.color,
          };
        });

        return {
          bars,
          centerX,
          hitX: groupStartX,
          hitW: Math.max(groupW, 1),
          label: p.label,
          values: p.values,
          hasData,
          showLabel: labelIndexSet.has(gi),
        };
      });

      const step = range / GRID_COUNT;
      const gridLines = Array.from(
        { length: GRID_COUNT + 1 },
        (_, i) => ({
          y: toY(minVal + step * i),
          value: minVal + step * i,
        })
      );

      return {
        W,
        H,
        padT: T,
        padR: R,
        padB: B,
        padL: L,
        groups,
        gridLines,
        zeroY,
      };
    },
  },

  methods: {
    updateWidth() {
      if (this.$refs.wrap) {
        const w = this.$refs.wrap.clientWidth;
        if (w > 0) this.containerWidth = w;
      }
    },

    setSeriesVisible(key, visible) {
      this.hiddenSeries = { ...this.hiddenSeries, [key]: !visible };
    },

    formatAxisValue(v) {
      if (this.formatValue) return this.formatValue(v, "axis");
      const n = Number(v);
      if (Math.abs(n) >= 1_000_000)
        return (n / 1_000_000).toFixed(1) + "М";
      if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(0) + "К";
      return Math.round(n).toString();
    },

    formatTooltipValue(v) {
      if (this.formatValue) return this.formatValue(v, "tooltip");
      return Number(v).toLocaleString("ru-RU");
    },

    onMouseMove(event) {
      if (!this.chartData) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const svgX =
        ((event.clientX - rect.left) / rect.width) * this.chartData.W;

      // Ищем ближайшую ненулевую группу
      let closest = null;
      let minDist = Infinity;
      this.chartData.groups.forEach((g) => {
        if (!g.hasData) return;
        const d = Math.abs(svgX - g.centerX);
        if (d < minDist) {
          minDist = d;
          closest = g;
        }
      });
      if (!closest) return;

      const wrapRect = this.$refs.wrap.getBoundingClientRect();
      let left = event.clientX - wrapRect.left + 14;
      if (left + 190 > wrapRect.width)
        left = event.clientX - wrapRect.left - 200;

      this.tooltip = {
        visible: true,
        svgX: closest.centerX,
        left,
        top: Math.max(0, event.clientY - wrapRect.top - 60),
        label: closest.label,
        values: closest.values,
      };
    },
  },

  mounted() {
    this.updateWidth();
    this._ro = new ResizeObserver(() => {
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this._rafId = requestAnimationFrame(this.updateWidth);
    });
    this._ro.observe(this.$refs.wrap);
  },

  beforeUnmount() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._ro) this._ro.disconnect();
  },
};
</script>

<style scoped>
.bar-chart {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.bar-chart__svg {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}

.bar-chart__axis-label {
  font-size: 11px;
  fill: #aaa;
  font-family: "Roboto", sans-serif;
}

.bar-chart__overflow {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 40px 20px;
  color: #aaa;
  font-size: 14px;
  font-style: italic;
  text-align: center;
  border: 1.5px dashed #e5e7eb;
  border-radius: 10px;
}

.bar-chart__tooltip {
  position: absolute;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 10;
  min-width: 180px;
}

.bar-chart__tooltip-period {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  margin-bottom: 2px;
}

.bar-chart__tooltip-row {
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-dark);
}

.bar-chart__tooltip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
